import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Link, generatePath, useSearchParams } from 'react-router-dom';
import { Activity, AlertTriangle, Calendar, DollarSign, TrendingDown } from 'lucide-react';

import { extractWorkspaceId, useCalculateChurn } from '@/api/dashboard/useCalculateChurn';
import { useGenerateChurnSignals } from '@/api/dashboard/useGenerateChurnSignals';
import { useGetDashboardDataByWorkspace } from '@/api/dashboard/useDashboardDetailsByWorkspace';
import { useAppSelector } from '@/app/hooks';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { Button } from '@/component-library/primitives/Button/Button';
import SignalsLoader from '@/component-library/primitives/Loaders/SignalsLoader';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import SearchField from '@/component-library/widgets/SearchField/SearchField';
import Table from '@/component-library/widgets/Table/Table';
import { usePageTracking } from '@/hooks/useTracking';
import { ChurnSignal } from '@/models/DashboardData';
import { AppRoutes } from '@/utils/routes/router';
import { firstChurnSignalGuid } from '@/utils/churnSignalUuid';
import { normalizeSignalStatus } from '@/utils/signalStatus';

import ChurnSignalModal from '../DashboardPage/Components/ChurnSignalModal';

import { SignalsFilterModal, SignalsFilters } from './SignalsFilterModal';

type SignalsListMode = 'active' | 'dismissed';

const SignalsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = extractWorkspaceId(workspace);
  const calculateChurn = useCalculateChurn();
  const generateSignals = useGenerateChurnSignals();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' as ToastType | '' });
  const [selectedSignal, setSelectedSignal] = useState<ChurnSignal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SignalsFilters>({
    priority: null,
    status: null,
    riskScoreFrom: null,
    riskScoreTo: null,
  });

  const listMode: SignalsListMode = searchParams.get('view') === 'not-relevant' ? 'dismissed' : 'active';

  const setListMode = (mode: SignalsListMode) => {
    if (mode === 'dismissed') {
      setSearchParams({ view: 'not-relevant' });
    } else {
      setSearchParams({});
    }
  };

  // Track page view
  usePageTracking('signals');

  // Status filter only applies on the Active tab; a leftover "new"/"resolved" filter hides all dismissed rows.
  useEffect(() => {
    if (listMode === 'dismissed') {
      setFilters((f) => ({ ...f, status: null }));
    }
  }, [listMode]);

  const columnHelper = useMemo(() => createColumnHelper<ChurnSignal>(), []);

  // Calculate date range for the last 30 days
  const { startTime, endTime } = useMemo(() => {
    const now = new Date();
    const endDate = new Date(now);
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - 30);

    // Use fixed times to prevent constant changes
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    return {
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
    };
  }, []);

  const { data: dashboardData, isLoading } = useGetDashboardDataByWorkspace(startTime, endTime);

  // Signals can be in signals.recent or churnSignals (for backward compatibility)
  const allSignals: ChurnSignal[] = useMemo(() => {
    const recent = (dashboardData as { signals?: { recent?: unknown } } | undefined)?.signals?.recent;
    if (Array.isArray(recent)) return recent as ChurnSignal[];
    const churn = dashboardData?.churnSignals;
    if (Array.isArray(churn)) return churn;
    return [];
  }, [dashboardData]);

  useEffect(() => {
    const sid = searchParams.get('signalId');
    if (!sid || isLoading || !allSignals.length) return;
    const match = allSignals.find((s) => firstChurnSignalGuid(s.id, s.signalId) === sid);
    if (!match) return;
    setSelectedSignal(match);
    setIsModalOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('signalId');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, allSignals, isLoading, setSearchParams]);

  const getSignalTypeIcon = (signalType: string) => {
    switch (signalType) {
      case 'high_risk_churn':
        return <AlertTriangle className="w-4 h-4 text-custom-red" />;
      case 'medium_risk_churn':
        return <AlertTriangle className="w-4 h-4 text-custom-yellow" />;
      case 'low_activity':
        return <TrendingDown className="w-4 h-4 text-custom-yellow" />;
      case 'payment_failure':
        return <DollarSign className="w-4 h-4 text-custom-red" />;
      case 'subscription_downgrade':
        return <TrendingDown className="w-4 h-4 text-custom-yellow" />;
      default:
        return <Activity className="w-4 h-4 text-custom-darkSilver" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSignalClick = useCallback((signal: ChurnSignal) => {
    setSelectedSignal(signal);
    setIsModalOpen(true);
  }, []);

  const columns = useMemo(
    () => [
    columnHelper.accessor('title', {
      header: () => <div className="text-custom-black font-medium">Signal</div>,
      cell: (info) => (
        <div
          className="flex items-center cursor-pointer p-2 -m-2 rounded"
          onClick={() => handleSignalClick(info.row.original)}
        >
          {getSignalTypeIcon(info.row.original.signalType)}
          <div className="ml-3">
            <div className="text-sm font-medium text-custom-black hover:text-custom-blue hover:underline">
              {info.renderValue()}
            </div>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor('userName', {
      header: () => <div className="text-custom-black font-medium">Customer</div>,
      cell: (info) => {
        const row = info.row.original;
        const name = info.renderValue() || row.userEmail || '—';
        return (
          <div onClick={(e) => e.stopPropagation()}>
            {row.userId ? (
              <Link
                to={generatePath(AppRoutes.memberProfile, { id: row.userId })}
                className="text-sm font-medium text-custom-blue hover:text-custom-darkBlue hover:underline"
              >
                {name}
              </Link>
            ) : (
              <div className="text-sm font-medium text-custom-black">{name}</div>
            )}
            <div className="text-sm text-custom-darkSilver">{row.userEmail}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor('priority', {
      header: () => <div className="text-custom-black font-medium">Priority</div>,
      cell: (info) => {
        const priority = info.renderValue() || 'low';
        const getPriorityColor = (level: string) => {
          switch (level.toLowerCase()) {
            case 'critical':
            case 'high':
              return 'bg-custom-red/10 text-custom-red border-custom-red/20';
            case 'medium':
              return 'bg-custom-yellow/10 text-custom-yellow border-custom-yellow/20';
            case 'low':
              return 'bg-custom-green/10 text-custom-green border-custom-green/20';
            default:
              return 'bg-custom-blue/10 text-custom-blue border-custom-blue/20';
          }
        };
        return (
          <span className={`text-xs px-2 py-1 rounded-md ${getPriorityColor(priority)}`}>{priority.toLowerCase()}</span>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: () => <div className="text-custom-black font-medium">Status</div>,
      cell: (info) => {
        const st = normalizeSignalStatus(info.row.original);
        const getStatusColor = (level: string) => {
          switch (level) {
            case 'new':
              return 'text-custom-blue';
            case 'in_progress':
              return 'text-custom-yellow';
            case 'resolved':
              return 'text-custom-green';
            case 'dismissed':
              return 'text-custom-darkSilver';
            default:
              return 'text-custom-darkSilver';
          }
        };
        const label = st === 'dismissed' ? 'not relevant' : st.replace(/_/g, ' ') || '—';
        return <span className={`font-medium ${getStatusColor(st)}`}>{label}</span>;
      },
    }),
    columnHelper.accessor('riskScore', {
      header: () => <div className="text-custom-black font-medium">Risk Score</div>,
      cell: (info) => {
        const score = info.renderValue() || 0;
        const getScoreColor = (score: number) => {
          if (score >= 60) return 'text-custom-red'; // High risk (60-100)
          if (score >= 30) return 'text-custom-yellow'; // Medium risk (30-59)
          return 'text-custom-green'; // Low risk (0-29)
        };
        return <span className={`font-medium ${getScoreColor(score)}`}>{score}%</span>;
      },
    }),
    columnHelper.accessor('createdAt', {
      header: () => <div className="text-custom-black font-medium">Created</div>,
      cell: (info) => (
        <div className="flex items-center text-sm text-custom-darkSilver">
          <Calendar className="w-4 h-4 mr-1" />
          {formatDate(info.renderValue() || '')}
        </div>
      ),
    }),
    ...(listMode === 'dismissed'
      ? [
          columnHelper.accessor((row) => row.dismissedReason ?? '', {
            id: 'dismissedReason',
            header: () => <div className="text-custom-black font-medium">Why not relevant</div>,
            cell: (info) => (
              <span className="text-xs text-custom-darkSilver max-w-[min(24rem,40vw)] line-clamp-3">
                {info.getValue() || '—'}
              </span>
            ),
          }),
        ]
      : []),
    ],
    [columnHelper, listMode, handleSignalClick],
  );

  const filteredSignals = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const statusFilter = listMode === 'dismissed' ? null : filters.status;

    return allSignals.filter((signal) => {
      const st = normalizeSignalStatus(signal);
      if (listMode === 'dismissed') {
        if (st !== 'dismissed') return false;
      } else if (st === 'dismissed') {
        return false;
      }

      const matchesSearch =
        !q ||
        signal.title.toLowerCase().includes(q) ||
        signal.description.toLowerCase().includes(q) ||
        signal.userName.toLowerCase().includes(q) ||
        signal.userEmail.toLowerCase().includes(q) ||
        (signal.dismissedReason || '').toLowerCase().includes(q);

      if (!matchesSearch) return false;

      if (filters.priority && signal.priority?.toLowerCase() !== filters.priority.toLowerCase()) {
        return false;
      }

      if (statusFilter && normalizeSignalStatus(signal) !== statusFilter.toLowerCase().replace(/-/g, '_')) {
        return false;
      }

      const riskScore = signal.riskScore || 0;
      if (filters.riskScoreFrom !== null && riskScore < filters.riskScoreFrom) {
        return false;
      }
      if (filters.riskScoreTo !== null && riskScore > filters.riskScoreTo) {
        return false;
      }

      return true;
    });
  }, [allSignals, searchTerm, filters, listMode]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSignal(null);
  };

  const handleCalculateChurn = () => {
    calculateChurn.mutate(undefined, {
      onSuccess: (res) => {
        setToastInfo({
          message: `${res.message} (${res.membersUpdated} members).`,
          toastType: 'success',
        });
        setToastOpen(true);
      },
      onError: () => {
        setToastInfo({ message: 'Could not calculate churn. Try again.', toastType: 'error' });
        setToastOpen(true);
      },
    });
  };

  const handleGenerateSignals = () => {
    generateSignals.mutate(undefined, {
      onSuccess: (res) => {
        setToastInfo({
          message: `${res.message} Created ${res.result.signalsCreated}, updated ${res.result.signalsUpdated} (${res.result.totalProcessed} members).`,
          toastType: 'success',
        });
        setToastOpen(true);
      },
      onError: () => {
        setToastInfo({ message: 'Could not generate signals. Try again.', toastType: 'error' });
        setToastOpen(true);
      },
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.priority !== null ||
      filters.status !== null ||
      filters.riskScoreFrom !== null ||
      filters.riskScoreTo !== null
    );
  }, [filters]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex border-b border-custom-lightSilver mb-4">
        <button
          type="button"
          onClick={() => setListMode('active')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            listMode === 'active'
              ? 'border-custom-blue text-custom-black'
              : 'border-transparent text-custom-darkSilver hover:text-custom-black'
          }`}
        >
          Active signals
        </button>
        <button
          type="button"
          onClick={() => setListMode('dismissed')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            listMode === 'dismissed'
              ? 'border-custom-blue text-custom-black'
              : 'border-transparent text-custom-darkSilver hover:text-custom-black'
          }`}
        >
          Not relevant
        </button>
      </div>

      {/* Header with search */}
      <div className="flex flex-row items-center mb-4 gap-3">
        <div className="flex-grow justify-start min-w-0">
          <SearchField
            initialValue={searchTerm}
            setSearchValue={setSearchTerm}
            withFilter={true}
            onFilterClick={() => setIsFilterModalOpen(true)}
            hasActiveFilters={hasActiveFilters}
            filterTooltipContent={
              <div className="text-sm text-gray-800">
                <div className="font-semibold mb-1">Searchable columns:</div>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Signal</li>
                  <li>Customer Name</li>
                  <li>Customer Email</li>
                </ul>
              </div>
            }
          />
        </div>
        <div className="flex flex-row gap-2 shrink-0">
          <Button
            type="button"
            variant="secondary"
            size="small"
            isLoading={calculateChurn.isLoading}
            disabled={!workspaceId || calculateChurn.isLoading || generateSignals.isLoading}
            onClick={handleCalculateChurn}
            className="whitespace-nowrap"
          >
            Calculate Churn
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="small"
            isLoading={generateSignals.isLoading}
            disabled={!workspaceId || generateSignals.isLoading || calculateChurn.isLoading}
            onClick={handleGenerateSignals}
            className="whitespace-nowrap"
            title="Creates or updates signals from audience risk scores (run Calculate Churn first)"
          >
            Generate signals
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <SignalsLoader />
      ) : filteredSignals && filteredSignals.length > 0 ? (
        <Table columns={columns} data={filteredSignals ?? []} />
      ) : (
        <NoDataBoard
          description={
            allSignals.length === 0
              ? 'No churn signals detected yet.'
              : listMode === 'dismissed'
              ? 'No signals marked as not relevant.'
              : 'Try adjusting your search or filter criteria.'
          }
        />
      )}

      {/* Signal Detail Modal */}
      <ChurnSignalModal isOpen={isModalOpen} onClose={handleCloseModal} signal={selectedSignal} />

      {/* Filter Modal */}
      <SignalsFilterModal
        isOpen={isFilterModalOpen}
        filters={filters}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={(newFilters) => setFilters(newFilters)}
        onClear={() =>
          setFilters({
            priority: null,
            status: null,
            riskScoreFrom: null,
            riskScoreTo: null,
          })
        }
      />

      <ProvideToasts>
        <Toast
          isOpen={toastOpen}
          setOpen={setToastOpen}
          message={toastInfo.message}
          toastType={(toastInfo.toastType || 'default') as ToastType}
        />
      </ProvideToasts>
    </div>
  );
};

export default SignalsPage;
